import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import { hiddenSet } from './dbColumnVisibility';

/**
 * Choose which grid columns are drawn.
 *
 * A modal rather than a dropdown because wide tables are exactly the case that
 * needs this — a hundred-column table wants a search box and room to scroll,
 * neither of which fits a popover anchored to a header cell.
 *
 * Selection is applied on Apply, not per click: toggling twenty columns one at
 * a time would re-render (and on a fixed-layout table, re-measure) the grid
 * twenty times.
 */
export default function DbColumnPickerModal({ open, columns, hidden, onApply, onClose }) {
  const initial = useMemo(() => {
    const set = hiddenSet(hidden);
    return new Set(columns.filter((c) => !set.has(c)));
  }, [columns, hidden]);

  const [visible, setVisible] = useState(initial);
  const [search, setSearch] = useState('');

  // Every open starts from what the grid is currently showing. Without this a
  // cancelled session would leave its toggles staged, and the next open would
  // present them as though they were the live state.
  useEffect(() => {
    if (!open) return;
    setVisible(initial);
    setSearch('');
  }, [open, initial]);

  if (!open) return null;

  const needle = search.trim().toLowerCase();
  const shown = needle ? columns.filter((c) => c.toLowerCase().includes(needle)) : columns;
  const noneSelected = visible.size === 0;

  const toggle = (col) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Choose columns"
      subtitle={`${visible.size} of ${columns.length} shown`}
      size="md"
    >
      <div className="dbm-colpick">
        <input
          type="text"
          className="dbm-colpick-search"
          placeholder="Filter columns"
          aria-label="Filter columns"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="dbm-colpick-actions">
          <button
            type="button"
            className="dbm-pager-btn"
            onClick={() => setVisible(new Set(columns))}
          >
            Show all
          </button>
          <button
            type="button"
            className="dbm-pager-btn"
            // Only the filtered subset, so "search + hide these" is one gesture
            // on a wide table.
            onClick={() => setVisible((prev) => {
              const next = new Set(prev);
              for (const c of shown) next.delete(c);
              return next;
            })}
          >
            Hide {needle ? 'matching' : 'all'}
          </button>
        </div>
        <OsScroll className="dbm-colpick-list">
          {shown.length === 0 ? (
            <div className="dbm-colpick-empty">No column matches “{search}”</div>
          ) : (
            shown.map((col) => (
              <label key={col} className="dbm-colpick-row">
                <input
                  type="checkbox"
                  className="checkbox-brand"
                  checked={visible.has(col)}
                  onChange={() => toggle(col)}
                />
                <span className="dbm-colpick-name">{col}</span>
              </label>
            ))
          )}
        </OsScroll>
        <div className="modal-footer-row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <span className="dbm-colpick-note">
            {noneSelected ? 'Keep at least one column visible.' : ''}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
            disabled={noneSelected}
            onClick={() => {
              onApply([...visible]);
              onClose();
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
}
